import React, { createContext, useContext, useState, useEffect } from 'react';
import useLocalStorage from '../hooks/useLocalStorage';
import { supabase, isSupabaseConfigured } from '../lib/supabase';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useLocalStorage('pokerTrackerUser', null);
  const [isLoading, setIsLoading] = useState(true);
  const [session, setSession] = useState(null);

  // Check if Supabase is configured
  const useSupabaseAuth = isSupabaseConfigured();

  useEffect(() => {
    const checkAuth = async () => {
      if (useSupabaseAuth) {
        try {
          // Get current session
          const { data: { session: currentSession } } = await supabase.auth.getSession();
          setSession(currentSession);
          
          if (currentSession?.user) {
            setUser({
              id: currentSession.user.id,
              email: currentSession.user.email,
              name: currentSession.user.user_metadata?.name || currentSession.user.email,
            });
          } else {
            setUser(null);
          }

          // Listen for auth changes
          const { data: { subscription } } = supabase.auth.onAuthStateChange(
            async (event, newSession) => {
              setSession(newSession);
              if (newSession?.user) {
                setUser({
                  id: newSession.user.id,
                  email: newSession.user.email,
                  name: newSession.user.user_metadata?.name || newSession.user.email,
                });
              } else {
                setUser(null);
              }
            }
          );

          return () => {
            subscription.unsubscribe();
          };
        } catch (error) {
          console.error('Error checking auth:', error);
          setUser(null);
        }
      }
      setIsLoading(false);
    };

    checkAuth().finally(() => {
      setIsLoading(false);
    });
  }, [useSupabaseAuth]);

  const login = async (email, password) => {
    if (useSupabaseAuth) {
      try {
        const { data, error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (error) throw error;

        if (data?.user) {
          const userData = {
            id: data.user.id,
            email: data.user.email,
            name: data.user.user_metadata?.name || data.user.email,
          };
          setUser(userData);
          return userData;
        }
      } catch (error) {
        throw new Error(error.message || 'Failed to sign in. Please check your credentials.');
      }
    } else {
      // Demo mode fallback
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          if (email === 'demo@example.com' && password === 'demo123') {
            const userData = {
              id: Date.now().toString(),
              email: email,
              name: 'Demo User',
              loginTime: new Date().toISOString()
            };
            setUser(userData);
            resolve(userData);
          } else {
            reject(new Error('Invalid credentials. Use demo@example.com / demo123 for demo mode.'));
          }
        }, 800);
      });
    }
  };

  const signUp = async (email, password, name) => {
    if (useSupabaseAuth) {
      try {
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: {
              name: name,
            }
          }
        });

        if (error) throw error;

        // Check if there's a session (user is confirmed)
        // If email confirmation is required, there won't be a session yet
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session?.user) {
          // User is confirmed and has a session (email confirmation disabled or auto-confirmed)
          const userData = {
            id: session.user.id,
            email: session.user.email,
            name: name || session.user.email,
          };
          setUser(userData);
          return { ...userData, needsConfirmation: false };
        } else if (data?.user) {
          // User created but needs to confirm email
          // Don't set user or create session - they need to confirm email first
          // Return a special indicator that email confirmation is needed
          return { 
            needsConfirmation: true, 
            email: data.user.email,
            message: 'Please check your email to confirm your account before signing in.'
          };
        }
      } catch (error) {
        throw new Error(error.message || 'Failed to create account. Please try again.');
      }
    } else {
      // Demo mode - just create a local user
      return new Promise((resolve) => {
        setTimeout(() => {
          const userData = {
            id: Date.now().toString(),
            email: email,
            name: name || 'Demo User',
            loginTime: new Date().toISOString()
          };
          setUser(userData);
          resolve(userData);
        }, 800);
      });
    }
  };

  const logout = async () => {
    if (useSupabaseAuth) {
      try {
        await supabase.auth.signOut();
      } catch (error) {
        console.error('Error signing out:', error);
      }
    }
    setUser(null);
    setSession(null);
  };

  const isAuthenticated = () => {
    return !!user;
  };

  const value = {
    user,
    session,
    isLoading,
    login,
    signUp,
    logout,
    isAuthenticated,
    useSupabaseAuth
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
